import { useState } from 'react';
import { X, Plus, Trash2, Tag } from 'lucide-react';
import type { EventTag, TagType } from '@peles/shared';
import {
  useEventTags,
  useCreateEventTag,
  useUpdateEventTag,
  useDeleteEventTag,
} from '../../hooks/useEvents';
import { useDialog } from '../../context/DialogContext';

interface EventTagsModalProps {
  eventId: string;
  onClose: () => void;
}

const TAG_TYPE_LABELS: Record<TagType, string> = {
  TEXT: 'Text',
  DATE: 'Date',
  NUMBER: 'Number',
};

function TagValueInput({
  type,
  value,
  onChange,
  placeholder,
  className,
}: {
  type: TagType;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  if (type === 'DATE') {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className}
      />
    );
  }
  if (type === 'NUMBER') {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
      />
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={className}
    />
  );
}

function TagRow({ tag, eventId }: { tag: EventTag; eventId: string }) {
  const { confirm } = useDialog();
  const updateTag = useUpdateEventTag(eventId);
  const deleteTag = useDeleteEventTag(eventId);
  const [value, setValue] = useState(tag.value ?? '');
  const [dirty, setDirty] = useState(false);

  function handleChange(v: string) {
    setValue(v);
    setDirty(true);
  }

  function handleSave() {
    updateTag.mutate({ tagId: tag.id, updates: { value: value || null } });
    setDirty(false);
  }

  async function handleDelete() {
    if (
      await confirm({
        title: 'Delete Tag',
        message: `Delete tag "${tag.code}"?`,
        confirmLabel: 'Delete',
      })
    ) {
      deleteTag.mutate(tag.id);
    }
  }

  return (
    <div
      className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0"
      data-testid={`tag-row-${tag.id}`}
    >
      <div className="w-36 shrink-0">
        <code className="text-xs font-mono bg-gray-100 text-primary-700 px-1.5 py-0.5 rounded">
          {`{${tag.code}}`}
        </code>
      </div>
      <div className="w-32 shrink-0 text-xs text-gray-500 truncate" title={tag.description}>
        {tag.description}
      </div>
      <span className="w-14 shrink-0 text-xs text-gray-400">{TAG_TYPE_LABELS[tag.type]}</span>
      <div className="flex-1">
        <TagValueInput
          type={tag.type}
          value={value}
          onChange={handleChange}
          placeholder="Enter value..."
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {dirty && (
        <button
          onClick={handleSave}
          disabled={updateTag.isPending}
          className="px-2 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50 shrink-0"
          data-testid={`save-tag-${tag.id}`}
        >
          Save
        </button>
      )}
      <button
        onClick={handleDelete}
        className="text-gray-400 hover:text-red-600 p-0.5 shrink-0"
        aria-label="Delete tag"
        data-testid={`delete-tag-${tag.id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

const EMPTY_FORM = { code: '', description: '', type: 'TEXT' as TagType, value: '' };

export function EventTagsModal({ eventId, onClose }: EventTagsModalProps) {
  const { data: tags = [], isLoading } = useEventTags(eventId);
  const createTag = useCreateEventTag(eventId);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  function handleAdd() {
    if (!form.code.trim() || !form.description.trim()) return;
    createTag.mutate(
      {
        code: form.code.trim().toUpperCase().replace(/\s+/g, '_'),
        description: form.description.trim(),
        type: form.type,
        value: form.value || undefined,
      },
      {
        onSuccess: () => {
          setForm(EMPTY_FORM);
          setShowAdd(false);
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      data-testid="event-tags-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Tag size={18} className="text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Event Tags</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
            data-testid="close-tags-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tag List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="text-xs text-gray-500 mb-3">
            Use <code className="bg-gray-100 px-1 rounded">{'{TAG_CODE}'}</code> in timeline fields
            to insert tag values when printing.
          </p>

          {/* Column headers */}
          {tags.length > 0 && (
            <div className="flex items-center gap-2 pb-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
              <div className="w-36 shrink-0">Code</div>
              <div className="w-32 shrink-0">Description</div>
              <div className="w-14 shrink-0">Type</div>
              <div className="flex-1">Value</div>
              <div className="w-10 shrink-0" />
            </div>
          )}

          {isLoading ? (
            <p className="text-sm text-gray-500 py-4">Loading tags...</p>
          ) : tags.length === 0 && !showAdd ? (
            <div className="text-center py-8 text-gray-400">
              <Tag size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No tags yet. Add your first tag below.</p>
            </div>
          ) : (
            <div>
              {tags.map((tag) => (
                <TagRow key={tag.id} tag={tag} eventId={eventId} />
              ))}
            </div>
          )}

          {/* Add form */}
          {showAdd && (
            <div
              className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
              data-testid="add-tag-form"
            >
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                    }))
                  }
                  placeholder="CODE"
                  className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-testid="add-tag-code"
                />
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Description"
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-testid="add-tag-description"
                />
                <select
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as TagType }))}
                  className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  data-testid="add-tag-type"
                >
                  <option value="TEXT">Text</option>
                  <option value="DATE">Date</option>
                  <option value="NUMBER">Number</option>
                </select>
              </div>
              <div className="flex gap-2 mb-2">
                <TagValueInput
                  type={form.type}
                  value={form.value}
                  onChange={(v) => setForm((f) => ({ ...f, value: v }))}
                  placeholder="Initial value (optional)"
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAdd}
                  disabled={!form.code.trim() || !form.description.trim() || createTag.isPending}
                  className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
                  data-testid="confirm-add-tag"
                >
                  Add Tag
                </button>
                <button
                  onClick={() => {
                    setShowAdd(false);
                    setForm(EMPTY_FORM);
                  }}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                  data-testid="cancel-add-tag"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center">
          {!showAdd && (
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
              data-testid="add-tag-button"
            >
              <Plus size={14} />
              Add Tag
            </button>
          )}
          <div className="ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
